import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const NAV_LINKS = [
  { label: "Home", href: "/" },
  {
    label: "Services",
    href: "/services",
    children: [
      { label: "Tree Removal", href: "/services/tree-removal" },
      { label: "Tree Trimming", href: "/services/tree-trimming" },
      { label: "Stump Grinding", href: "/services/stump-grinding" },
    ],
  },
  { label: "About", href: "/about" },
  { label: "Service Areas", href: "/service-areas" },
  { label: "Gallery", href: "/gallery" },
  { label: "Reviews", href: "/reviews" },
  { label: "Contact", href: "/contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location]);

  return (
    <>
      {/* Top urgency bar */}
      <div className="bg-[#1a3a2e] text-white py-2.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <i className="ri-alarm-warning-fill text-[#e8632a]"></i>
            <span className="font-semibold hidden sm:inline">24/7 Emergency Tree Service Available</span>
            <span className="font-semibold sm:hidden">24/7 Emergency Service</span>
          </div>
          <div className="flex items-center gap-5">
            <span className="hidden md:flex items-center gap-1.5 text-sm text-white/80">
              <i className="ri-time-line text-[#e8632a]"></i>
              Mon–Sat 7am–7pm
            </span>
            <a
              href="tel:+15407764488"
              className="flex items-center gap-2 font-black text-base text-white hover:text-[#e8632a] transition-colors cursor-pointer"
            >
              <i className="ri-phone-fill text-[#e8632a] text-lg"></i>
              (540) 776-4488
            </a>
          </div>
        </div>
      </div>

      {/* Main header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-white border-b-2 border-[#e8632a]"
            : "bg-white border-b border-gray-100"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between py-3">
          {/* Logo — larger, more authoritative */}
          <Link to="/" className="flex-shrink-0 cursor-pointer">
            <div className="flex flex-col leading-none">
              <div className="flex items-baseline gap-1.5">
                <span className="text-[#1e1e1e] font-black text-[22px] tracking-[-0.5px] uppercase">
                  Diversified
                </span>
                <span className="text-[#e8632a] font-black text-[22px] tracking-[-0.5px] uppercase">
                  Tree Services
                </span>
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-[#1a3a2e] font-bold text-[11px] tracking-[2px] uppercase">
                  LLC
                </span>
                <span className="w-1 h-1 bg-[#e8632a] rounded-full inline-block"></span>
                <span className="text-gray-500 font-semibold text-[10px] tracking-[1.5px] uppercase">
                  Licensed &amp; Insured · Since 2008
                </span>
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-0.5">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div
                  key={link.href}
                  className="relative"
                  onMouseEnter={() => setServicesOpen(true)}
                  onMouseLeave={() => setServicesOpen(false)}
                >
                  <button className="flex items-center gap-1 px-3 py-2 text-[13px] font-bold uppercase tracking-wider text-[#1e1e1e] hover:text-[#e8632a] transition-colors cursor-pointer whitespace-nowrap">
                    {link.label}
                    <i className="ri-arrow-down-s-line text-base"></i>
                  </button>
                  {servicesOpen && (
                    <div className="absolute top-full left-0 bg-white rounded-xl py-2 min-w-52 border-t-[3px] border-[#e8632a] z-50" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className="flex items-center gap-2.5 px-4 py-3 text-sm font-semibold text-[#1e1e1e] hover:bg-[#fff5f0] hover:text-[#e8632a] transition-colors cursor-pointer"
                        >
                          <i className="ri-arrow-right-s-line text-[#e8632a]"></i>
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`px-3 py-2 text-[13px] font-bold uppercase tracking-wider transition-colors cursor-pointer whitespace-nowrap ${
                    location.pathname === link.href
                      ? "text-[#e8632a]"
                      : "text-[#1e1e1e] hover:text-[#e8632a]"
                  }`}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* Right CTA cluster */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/contact"
              className="px-4 py-2.5 rounded-lg border-2 border-[#1a3a2e] text-[#1a3a2e] text-[13px] font-black uppercase tracking-wide hover:bg-[#1a3a2e] hover:text-white transition-all cursor-pointer whitespace-nowrap"
            >
              Free Estimate
            </Link>
            <a
              href="tel:+15407764488"
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#e8632a] text-white text-[13px] font-black uppercase tracking-wide hover:bg-[#d0541e] transition-all cursor-pointer whitespace-nowrap"
              style={{ letterSpacing: "0.5px" }}
            >
              <i className="ri-phone-fill text-base"></i>
              Call Now
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2 text-[#1e1e1e] cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-2xl ${mobileOpen ? "ri-close-line" : "ri-menu-3-line"}`}></i>
          </button>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t-2 border-[#e8632a] px-4 pb-6">
            {NAV_LINKS.map((link) => (
              <div key={link.href}>
                <Link
                  to={link.href}
                  className="block py-3 text-sm font-black uppercase tracking-wider text-[#1e1e1e] border-b border-gray-100 cursor-pointer"
                >
                  {link.label}
                </Link>
                {link.children?.map((child) => (
                  <Link
                    key={child.href}
                    to={child.href}
                    className="block py-2.5 pl-4 text-sm font-semibold text-gray-500 hover:text-[#e8632a] cursor-pointer border-b border-gray-50"
                  >
                    {child.label}
                  </Link>
                ))}
              </div>
            ))}
            <div className="flex flex-col gap-3 mt-5">
              <Link
                to="/contact"
                className="text-center py-3 rounded-lg border-2 border-[#1a3a2e] text-[#1a3a2e] font-black uppercase tracking-wide cursor-pointer"
              >
                Free Estimate
              </Link>
              <a
                href="tel:+15407764488"
                className="flex items-center justify-center gap-2 py-3.5 rounded-lg bg-[#e8632a] text-white font-black uppercase tracking-wide cursor-pointer text-lg"
              >
                <i className="ri-phone-fill"></i>
                (540) 776-4488
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Floating mobile call button */}
      <a
        href="tel:+15407764488"
        className="fixed bottom-6 right-6 z-50 lg:hidden w-16 h-16 flex items-center justify-center bg-[#e8632a] text-white rounded-full cursor-pointer"
        aria-label="Call Diversified Tree Services"
        style={{ boxShadow: "0 0 0 0 rgba(232,99,42,0.5)", animation: "pulse-orange 2s infinite" }}
      >
        <i className="ri-phone-fill text-2xl"></i>
      </a>
    </>
  );
}
